/**
 * OAuth2 authentication utilities
 * Common OAuth2 flow utilities for all apps
 */ function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _ts_generator(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
import { generateCodeVerifier, generateCodeChallenge } from './pkce';
// Default OAuth2 configuration
export var DEFAULT_OAUTH2_CONFIG = {
    clientId: 'app-client',
    responseType: 'code',
    scopes: [
        'openid',
        'profile',
        'email',
        'api.read',
        'offline_access'
    ]
};
/**
 * Generate a random state for CSRF protection
 */ export function generateOAuthState() {
    var array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, function(byte) {
        return byte.toString(16).padStart(2, '0');
    }).join('');
}
/**
 * Build OAuth2 authorization URL with PKCE and i18n support
 * @param config OAuth2 configuration
 * @param params OAuth2 flow parameters including language setting
 * @param codeChallenge PKCE code challenge
 * @returns Complete authorization URL with all necessary parameters
 */ export function buildAuthorizationUrl(config, params, codeChallenge) {
    var url = new URL("".concat(config.authServerUrl, "/oauth2/authorize"));
    // Required OAuth2 parameters
    url.searchParams.set('client_id', config.clientId);
    url.searchParams.set('response_type', config.responseType);
    url.searchParams.set('redirect_uri', config.redirectUri);
    url.searchParams.set('scope', config.scopes.join(' '));
    // PKCE parameters
    url.searchParams.set('code_challenge', codeChallenge);
    url.searchParams.set('code_challenge_method', 'S256');
    // State for CSRF protection
    if (params.state) {
        url.searchParams.set('state', params.state);
    }
    // Optional parameters
    if (params.provider && params.provider !== 'internal') {
        url.searchParams.set('identity_provider', params.provider);
    }
    if (params.prompt) {
        url.searchParams.set('prompt', params.prompt);
    }
    if (params.displayName) {
        url.searchParams.set('display_name', params.displayName);
    }
    if (params.displayIcon) {
        url.searchParams.set('display_icon', params.displayIcon);
    }
    // Language parameter for i18n support
    if (params.lang) {
        url.searchParams.set('lang', params.lang);
    }
    return url.toString();
}
/**
 * Initiate OAuth2 authentication flow with automatic language detection
 * Automatically detects current language from i18next, document.lang, or navigator
 * @param provider OAuth2 provider (kakao, google, naver, internal)
 * @param config Optional OAuth2 configuration overrides
 * @param prompt Optional prompt parameter (e.g., 'login' to force re-authentication)
 */ export function initiateOAuth2Flow() {
    return _initiateOAuth2Flow.apply(this, arguments);
}
function _initiateOAuth2Flow() {
    _initiateOAuth2Flow = _async_to_generator(function() {
        var provider, config, prompt, postLoginRedirect, fullConfig, currentLang, stateData, state, codeVerifier, codeChallenge, sessionId, originalUrl, authUrl;
        var _arguments = arguments;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    provider = _arguments.length > 0 && _arguments[0] !== void 0 ? _arguments[0] : 'kakao', config = _arguments.length > 1 ? _arguments[1] : void 0, prompt = _arguments.length > 2 ? _arguments[2] : void 0, postLoginRedirect = _arguments.length > 3 ? _arguments[3] : void 0;
                    fullConfig = {
                        clientId: (config === null || config === void 0 ? void 0 : config.clientId) || DEFAULT_OAUTH2_CONFIG.clientId,
                        responseType: (config === null || config === void 0 ? void 0 : config.responseType) || DEFAULT_OAUTH2_CONFIG.responseType,
                        scopes: (config === null || config === void 0 ? void 0 : config.scopes) || DEFAULT_OAUTH2_CONFIG.scopes,
                        authServerUrl: (config === null || config === void 0 ? void 0 : config.authServerUrl) || (typeof window !== 'undefined' ? window.location.origin : 'https://auth.ugot.uk'),
                        redirectUri: (config === null || config === void 0 ? void 0 : config.redirectUri) || (typeof window !== 'undefined' ? window.location.origin + '/oauth2/redirect' : 'http://localhost:3000/oauth2/redirect')
                    };
                    console.log("[OAuth2] Initiating ".concat(provider, " OAuth2 flow"));
                    // Get current language setting for i18n support
                    currentLang = typeof window !== 'undefined' ? localStorage.getItem('i18nextLng') || document.documentElement.lang || navigator.language.split('-')[0] || 'ko' : 'ko';
                    // Generate and store state (includes original URL)
                    stateData = {
                        random: generateOAuthState(),
                        originalUrl: postLoginRedirect || (typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/'),
                        provider: provider
                    };
                    state = btoa(JSON.stringify(stateData)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
                    if (typeof window !== 'undefined') {
                        localStorage.setItem('oauth2_state', state);
                    }
                    // Generate and store PKCE
                    codeVerifier = generateCodeVerifier();
                    return [
                        4,
                        generateCodeChallenge(codeVerifier)
                    ];
                case 1:
                    codeChallenge = _state.sent();
                    if (typeof window !== 'undefined') {
                        localStorage.setItem('pkce_code_verifier', codeVerifier);
                        // Generate session ID for duplicate prevention
                        sessionId = crypto.randomUUID();
                        localStorage.setItem('oauth2_session_id', sessionId);
                        originalUrl = stateData.originalUrl || '/';
                        localStorage.setItem('oauth2_post_login_redirect', originalUrl);
                    }
                    // Build authorization URL
                    authUrl = buildAuthorizationUrl(fullConfig, {
                        provider: provider,
                        state: state,
                        displayName: 'App',
                        displayIcon: '🔐',
                        lang: currentLang,
                        prompt: prompt
                    }, codeChallenge);
                    console.log('[OAuth2] Redirecting to:', authUrl);
                    // Redirect to auth server
                    if (typeof window !== 'undefined') {
                        window.location.href = authUrl;
                    }
                    return [
                        2
                    ];
            }
        });
    });
    return _initiateOAuth2Flow.apply(this, arguments);
}
/**
 * Login with redirect helper
 * Convenience function for backward compatibility
 */ export function loginWithRedirect() {
    var provider = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : 'kakao', config = arguments.length > 1 ? arguments[1] : void 0, prompt = arguments.length > 2 ? arguments[2] : void 0, postLoginRedirect = arguments.length > 3 ? arguments[3] : void 0;
    initiateOAuth2Flow(provider, config, prompt, postLoginRedirect).catch(function(error) {
        console.error('[OAuth2] Failed to initiate OAuth2 flow:', error);
        throw error;
    });
}
